// First-touch acquisition attribution. Captured once, on the visitor's very
// first arrival — on ANY page, not just the SPA — and persisted in a cookie
// (not localStorage: the static /blog/*.html pages aren't part of the React
// bundle and can't touch it, but they can set a cookie) so it survives SPA
// navigation, full-page reloads, and registration happening minutes/pages
// later. See public/attribution-capture.js for the twin of this capture
// logic that runs on the static blog pages before the React bundle ever
// loads; both read/write the same cookie so whichever runs first wins.
const COOKIE_NAME = 'lc_attribution';
const COOKIE_MAX_AGE_DAYS = 90;

function readCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

// Share the cookie between lifetimecloud.me and www.lifetimecloud.me so a
// landing/registration split across those two hosts doesn't lose the record.
// Omitted on localhost/dev/preview hosts, where an explicit Domain would
// just make the browser reject the cookie.
function cookieDomainAttr() {
  return /(^|\.)lifetimecloud\.me$/.test(window.location.hostname) ? '; domain=.lifetimecloud.me' : '';
}

function writeCookie(name, value) {
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax${cookieDomainAttr()}`;
}

// 2026-09-01 — mirrors public/attribution-capture.js's writeAll/readAny; see
// the long comment there for the incident (17 real users, 4 of them paying,
// arrived with ad parameters proven in the nginx logs and registered as
// 'direct' because the cookie was gone by then). The cookie stays the
// primary store; localStorage and sessionStorage are copies read back when
// it isn't there. Both writers must agree on the shape, so any change here
// belongs in that file too.
function writeAll(name, value) {
  writeCookie(name, value);
  try { window.localStorage.setItem(name, value); } catch {}
  try { window.sessionStorage.setItem(name, value); } catch {}
}

function readAny(name) {
  const fromCookie = readCookie(name);
  if (fromCookie) return fromCookie;
  let v = null;
  try { v = window.localStorage.getItem(name); } catch {}
  if (v) return v;
  try { v = window.sessionStorage.getItem(name); } catch {}
  return v || null;
}

// utm_source, if present, always wins. Otherwise a click ID or referrer
// identifies the platform even without UTMs (ad platforms often append only
// their own click ID, and the referrer survives even with no params at all —
// e.g. a plain link on reddit.com). Falls back to the referrer's bare host,
// or "direct" for a visit with no signal at all.
function computeSource({ utm_source, fbclid, rdt_cid, gclid, ttclid, referrer }) {
  if (utm_source) return utm_source.toLowerCase();
  if (rdt_cid) return 'reddit';

  let referrerHost = '';
  try { if (referrer) referrerHost = new URL(referrer).hostname.replace(/^www\./, ''); } catch {}

  if (/(^|\.)reddit\.com$/.test(referrerHost)) return 'reddit'; // covers reddit.com and out.reddit.com
  if (fbclid) return /(^|\.)instagram\.com$/.test(referrerHost) ? 'ig' : 'fb';
  if (gclid) return 'google';
  if (ttclid) return 'tiktok';
  if (referrerHost) return referrerHost;
  return 'direct';
}

// Call once at app bootstrap (see App.js), synchronously at module load —
// NOT inside a useEffect. AppRouter's own mount effect rewrites the URL back
// to "/" shortly after every load (history seeding for the PWA back button),
// so anything reading window.location later would see the wiped URL instead
// of the real landing page. Capturing here, before that effect runs, is what
// makes this safe.
// No-op if a first-touch record is already stored — first touch wins, a
// later page/visit shouldn't overwrite the landing page & campaign that
// actually brought the user here.
export function captureAttribution() {
  try {
    if (readAny(COOKIE_NAME)) return;

    const params = new URLSearchParams(window.location.search);
    const utm_source   = params.get('utm_source')   || null;
    const utm_medium   = params.get('utm_medium')   || null;
    const utm_campaign = params.get('utm_campaign') || null;
    const fbclid       = params.get('fbclid')       || null;
    const rdt_cid      = params.get('rdt_cid')      || null;
    const gclid        = params.get('gclid')        || null;
    const ttclid       = params.get('ttclid')       || null;
    const referrer     = document.referrer || null;

    const source = computeSource({ utm_source, fbclid, rdt_cid, gclid, ttclid, referrer });

    // landingPage is always worth recording (it's how we find out which blog
    // article/page actually converts), even on a plain organic/direct visit
    // with no utm/click-id — so, unlike source/campaign/medium, capture always
    // runs; there's no "nothing worth recording" bail-out any more.
    // Raw click IDs are kept alongside the computed `source` (which only ever
    // collapses to a short platform label) — fbclid in particular is needed
    // verbatim to build Meta's `fbc` Conversions API parameter later
    // (`fb.<subdomain_index>.<click_time_ms>.<fbclid>`), which is also why
    // capturedAt (the actual click time, not registration time) travels with
    // it — see getAttribution()'s attributionCapturedAt.
    writeAll(COOKIE_NAME, JSON.stringify({
      source,
      campaign: utm_campaign || null,
      medium: utm_medium || null,
      utmSource: utm_source || null,
      fbclid, rdtCid: rdt_cid, gclid, ttclid,
      referrer,
      landingPage: window.location.pathname,
      capturedAt: new Date().toISOString(),
    }));
  } catch {}
}

// Returns the full first-touch record to attach to a registration. Falls
// back to a live referrer/direct computation (source only — no first-touch
// landing page exists to report) if nothing was ever captured, e.g. cookies
// are blocked or the visitor registered before captureAttribution() ran.
export function getAttribution() {
  try {
    const raw = readAny(COOKIE_NAME);
    if (raw) {
      // Cookie gone but a copy survived — restore it so later reads (and the
      // static pages outside this bundle) see the same first touch.
      if (!readCookie(COOKIE_NAME)) { try { writeAll(COOKIE_NAME, raw); } catch {} }
      const { source, campaign, medium, utmSource, fbclid, rdtCid, gclid, ttclid, referrer, landingPage, capturedAt } = JSON.parse(raw);
      return {
        source: source || 'direct',
        campaign: campaign || null,
        medium: medium || null,
        utmSource: utmSource || null,
        fbclid: fbclid || null, rdtCid: rdtCid || null, gclid: gclid || null, ttclid: ttclid || null,
        referrer: referrer || null,
        landingPage: landingPage || null,
        attributionCapturedAt: capturedAt || null,
      };
    }
  } catch {}
  return {
    source: computeSource({ referrer: document.referrer || null }),
    campaign: null, medium: null, utmSource: null,
    fbclid: null, rdtCid: null, gclid: null, ttclid: null,
    referrer: document.referrer || null,
    landingPage: null,
    attributionCapturedAt: null,
  };
}

// ── Referral program (?ref=CODE) ────────────────────────────
// Separate cookie from acquisition attribution — a referral code is an
// explicit, unambiguous signal (unlike inferred utm/referrer source) and
// first-touch still wins so a later organic visit can't steal someone
// else's referral credit before the visitor actually registers.
const REFERRAL_COOKIE_NAME = 'lc_referral';

export function captureReferral() {
  try {
    if (readAny(REFERRAL_COOKIE_NAME)) return;
    const code = new URLSearchParams(window.location.search).get('ref');
    if (code && /^[A-Za-z0-9]{4,16}$/.test(code)) {
      writeAll(REFERRAL_COOKIE_NAME, code.toUpperCase());
    }
  } catch {}
}

export function getReferralCode() {
  try { return readAny(REFERRAL_COOKIE_NAME) || null; } catch { return null; }
}
